
import { useEffect } from "react";

interface ThemeColors {
  background: number[];
  foreground: number[];
  card: number[];
  cardForeground: number[];
  popover: number[];
  popoverForeground: number[];
  primary: number[];
  primaryForeground: number[];
  secondary: number[];
  secondaryForeground: number[];
  muted: number[];
  mutedForeground: number[];
  accent: number[];
  accentForeground: number[];
  destructive: number[];
  destructiveForeground: number[];
  border: number[];
  input: number[];
  ring: number[];
  sidebarBackground: number[];
  sidebarForeground: number[];
  sidebarPrimary: number[];
  sidebarPrimaryForeground: number[];
  sidebarAccent: number[];
  sidebarAccentForeground: number[];
  sidebarBorder: number[];
  sidebarRing: number[];
  success: number[];
  successForeground: number[];
  warning: number[];
  warningForeground: number[];
  // Layout-specific colors
  navbarBackground: number[];
  navbarForeground: number[];
  footerBackground: number[];
  footerForeground: number[];
  mainPageBackground: number[];
}

interface Theme {
  id: string;
  accountId: string;
  name: string;
  description?: string;
  light: ThemeColors;
  dark: ThemeColors;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ThemeProviderProps {
  theme: Theme | null;
  children: React.ReactNode;
}

/**
 * Theme Provider Component
 *
 * Applies custom theme colors as CSS variables to the document root.
 * If no theme is provided, uses the default CSS variables from globals.css.
 */
export function ThemeProvider({ theme, children }: ThemeProviderProps) {
  useEffect(() => {
    if (!theme || !theme.light || !theme.dark) return;

    const root = document.documentElement;

    // Determine which color mode to use (light or dark)
    const isDarkMode = root.classList.contains('dark');
    const colors = isDarkMode ? theme.dark : theme.light;

    // Apply theme colors as CSS variables (without hsl() wrapper for Tailwind compatibility)
    const applyColor = (variableName: string, hsl: number[]) => {
      root.style.setProperty(variableName, `${hsl[0]} ${hsl[1]}% ${hsl[2]}%`);
    };

    // Base colors
    applyColor('--background', colors.background);
    applyColor('--foreground', colors.foreground);

    // Card & Surface
    applyColor('--card', colors.card);
    applyColor('--card-foreground', colors.cardForeground);
    applyColor('--popover', colors.popover);
    applyColor('--popover-foreground', colors.popoverForeground);

    // Brand colors
    applyColor('--primary', colors.primary);
    applyColor('--primary-foreground', colors.primaryForeground);
    applyColor('--secondary', colors.secondary);
    applyColor('--secondary-foreground', colors.secondaryForeground);
    applyColor('--accent', colors.accent);
    applyColor('--accent-foreground', colors.accentForeground);

    // Muted & Destructive
    applyColor('--muted', colors.muted);
    applyColor('--muted-foreground', colors.mutedForeground);
    applyColor('--destructive', colors.destructive);
    applyColor('--destructive-foreground', colors.destructiveForeground);

    // Borders & Inputs
    applyColor('--border', colors.border);
    applyColor('--input', colors.input);
    applyColor('--ring', colors.ring);

    // Sidebar
    applyColor('--sidebar-background', colors.sidebarBackground);
    applyColor('--sidebar-foreground', colors.sidebarForeground);
    applyColor('--sidebar-primary', colors.sidebarPrimary);
    applyColor('--sidebar-primary-foreground', colors.sidebarPrimaryForeground);
    applyColor('--sidebar-accent', colors.sidebarAccent);
    applyColor('--sidebar-accent-foreground', colors.sidebarAccentForeground);
    applyColor('--sidebar-border', colors.sidebarBorder);
    applyColor('--sidebar-ring', colors.sidebarRing);

    // Status colors
    applyColor('--success', colors.success);
    applyColor('--success-foreground', colors.successForeground);
    applyColor('--warning', colors.warning);
    applyColor('--warning-foreground', colors.warningForeground);

    // Layout colors
    applyColor('--navbar-background', colors.navbarBackground);
    applyColor('--navbar-foreground', colors.navbarForeground);
    applyColor('--footer-background', colors.footerBackground);
    applyColor('--footer-foreground', colors.footerForeground);
    applyColor('--main-page-background', colors.mainPageBackground);

    // Cleanup function to reset styles when component unmounts
    return () => {
      const variables = [
        '--background', '--foreground',
        '--card', '--card-foreground',
        '--popover', '--popover-foreground',
        '--primary', '--primary-foreground',
        '--secondary', '--secondary-foreground',
        '--muted', '--muted-foreground',
        '--accent', '--accent-foreground',
        '--destructive', '--destructive-foreground',
        '--border', '--input', '--ring',
        '--sidebar-background', '--sidebar-foreground',
        '--sidebar-primary', '--sidebar-primary-foreground',
        '--sidebar-accent', '--sidebar-accent-foreground',
        '--sidebar-border', '--sidebar-ring',
        '--success', '--success-foreground',
        '--warning', '--warning-foreground',
        '--navbar-background', '--navbar-foreground',
        '--footer-background', '--footer-foreground',
        '--main-page-background',
      ];

      variables.forEach((variable) => {
        root.style.removeProperty(variable);
      });
    };
  }, [theme]);

  return <>{children}</>;
}
